import React, { useCallback, useEffect, useMemo, useState } from "react";
import PrimaryCard from "../common/cards/PrimaryCard";
import { useAssistance, useCameras, useChimeMeeting } from "../../hooks";
import { RemoteVideo, useRemoteVideoTileState } from "amazon-chime-sdk-component-library-react";
import { Box, Card, CardBody, Grid, GridItem, HStack, Icon, Text, VStack, keyframes, useDisclosure } from "@chakra-ui/react";
import { Trans, useTranslation } from "react-i18next";
import { CameraType } from "../../enums";
import moment from "moment";
import { PrimaryButton } from "../common/buttons";
import useMicrophone from "../../hooks/useMicrophone";
import {
  PiVideoCameraSlash,
} from "react-icons/pi";
import { motion } from "framer-motion";
import ParticipantInput from "../participants/ParticipantInput";
import { addAttendee } from "../../api/assistance";
import { useAuth } from "../../../hooks";
import "./CameraCard.css"
import { FaMicrophoneSlash } from "react-icons/fa";
import PhoneIcon from "../common/icons/PhoneIcon";
import MicrophoneIcon from "../common/icons/MicrophoneIcon";
import InteriorCameraIcon from "../common/icons/InteriorCameraIcon";
import ExteriorCameraIcon from "../common/icons/ExteriorCameraIcon";

const animationKeyframes = keyframes`
  0% { opacity: 0; }
  50% { opacity: 1; }
  100% { opacity: 0; }
`;

const animation = `${animationKeyframes} 4s ease-in-out infinite`;

const CameraCard = () => {
  const { t, i18n } = useTranslation();
  const { hasJoined, sendControlMessage } = useChimeMeeting();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { assistance } = useAssistance();
  const { bearer } = useAuth();
  const {
    selectedCameras,
    onSelectInteriorCameras,
    onSelectExteriorCameras,
    onSelectCamera,
    outCameras,
    inCameras,
    isCameraSelected,
  } = useCameras();
  const {
    isMuted,
    onMuteMicrophone,
    microphones,
    onSelectMicrophone,
    unMuteMicrophone,
    isMicrophoneSelected: microphoneSelected,
  } = useMicrophone();
  const [isClicking, setIsClicking] = useState(false);
  const [lastRequestStamp, setLastRequestStamp] = useState(moment());
  const [isExteriorCameraSelected, setIsExteriorCameraSelected] =
    useState(false);
  const [isInteriorCameraSelected, setIsInteriorCameraSelected] =
    useState(false);
  const [isMicrophoneSelected, setIsMicrophoneSelected] = useState(false);

  const isPanoCameraSelected = useMemo(
    () => selectedCameras?.includes(CameraType.PANO),
    [selectedCameras],
  );

  const language = useMemo(() => i18n?.language.slice(0, 2), [i18n?.language]);

  const onClickStart = useCallback(
    (event) => {
      setIsClicking(true);
      if (isPanoCameraSelected) {
        const relativeX = event.nativeEvent.offsetX / event.target.clientWidth;
        const relativeY =
          (1 - event.nativeEvent.offsetY / event.target.clientHeight) * 0.8;

        setLastRequestStamp(moment());
        sendControlMessage(
          JSON.stringify({
            x: relativeX.toFixed(3),
            y: relativeY.toFixed(3),
          }),
        );
      }
    },
    [isPanoCameraSelected, sendControlMessage],
  );

  const onClickEnd = useCallback(() => {
    setIsClicking(false);
    if (isPanoCameraSelected) {
      sendControlMessage(
        JSON.stringify({
          stop: true,
        }),
      );
    }
  }, [isPanoCameraSelected, sendControlMessage]);

  const onMove = useCallback(
    (event) => {
      if (isPanoCameraSelected && isClicking) {
        const relativeX = event.nativeEvent.offsetX / event.target.clientWidth;
        const relativeY =
          (1 - event.nativeEvent.offsetY / event.target.clientHeight) * 0.8;

        const newRequestStamp = moment();
        if (newRequestStamp.diff(lastRequestStamp, "milliseconds") > 100) {
          setLastRequestStamp(moment());
          sendControlMessage(
            JSON.stringify({
              x: relativeX.toFixed(3),
              y: relativeY.toFixed(3),
            }),
          );
        }
      }
    },
    [isClicking, isPanoCameraSelected, lastRequestStamp, sendControlMessage],
  );

  const onScroll = useCallback(
    (event) => {
      event.stopPropagation();
      if (isPanoCameraSelected) {
        const newRequestStamp = moment();

        if (newRequestStamp.diff(lastRequestStamp, "milliseconds") > 50) {
          setLastRequestStamp(moment());
          sendControlMessage(
            JSON.stringify({
              zoom: event.deltaY > 0 ? -1 : 1,
            }),
          );
        }
      }
    },
    [isPanoCameraSelected, lastRequestStamp, sendControlMessage],
  );

  useEffect(() => {
    if (isClicking) {
      window.addEventListener("mouseup", onClickEnd);
      return () => {
        window.removeEventListener("mouseup", onClickEnd);
      };
    }
  }, [isClicking, onClickEnd]);

  const onAddParticipantConfirm = useCallback((phoneNumber) => {
    addAttendee(phoneNumber, assistance?.assistance_request_id, language, bearer)
    onClose();
  }, [assistance?.assistance_request_id, language, bearer, onClose]);

  const { tiles } = useRemoteVideoTileState();

  const mainTile = useMemo(() => tiles?.[0] || null, [tiles]);

  const externalTiles = useMemo(() => tiles.slice(1) || [], [tiles]);

  return (
    <PrimaryCard
      id={"camera-card"}
      title={t("plugin.cameras.title")}
    >
      {hasJoined ? (
        <Box
          width={"full"}
          height={"full"}
        >
          {
            tiles.length === 0 ? (
              <Box backgroundColor={"black"} width={"full"} height={"full"} borderRadius={"7px"}>
                <VStack
                  as={motion.div}
                  animation={animation}
                  width={"full"}
                  height={"75%"}
                  justifyContent={"center"}
                  color={"white"}
                >
                  <Icon as={PiVideoCameraSlash} fontSize={"48px"} />
                  <Text fontSize={"18px"}>
                    <Trans>plugin.cameras.unavailable</Trans>
                  </Text>
                </VStack>
              </Box>
            ) : (
              <Grid templateRows={"repeat(1, 1fr)"} templateColumns={"repeat(3, 1fr)"} width={"full"} height={"full"} backgroundColor={"transparent"} borderRadius={"7px"} padding={0} gap={"8px"}>
                <GridItem colSpan={tiles.length > 1 ? 2 : 3}>
                  <Box position={"relative"} width={"full"} height={"full"}>
                    <Box as={RemoteVideo}
                      tileId={mainTile}
                      onMouseMove={onMove}
                      onMouseDown={onClickStart}
                      onWheel={onScroll}
                      zIndex={200}
                      borderRadius={"7px"}
                    />
                    <Box
                      position={"absolute"}
                      left={0}
                      top={0}
                      width={"full"}
                      height={"full"}
                      display={"flex"}
                      flexDirection={"column"}
                      justifyContent={"space-between"}
                      padding={"20px"}
                    >
                      <VStack
                        width={"full"}
                        height={"full"}
                        justifyContent={"flex-end"}
                        alignItems={"center"}
                      >
                        {((isExteriorCameraSelected &&
                          !isMicrophoneSelected &&
                          outCameras?.length > 1) || (
                            isInteriorCameraSelected &&
                            !isMicrophoneSelected &&
                            inCameras?.length > 1
                          ) || (isMicrophoneSelected && microphones?.length > 1)) && (
                            <Card backgroundColor={"#84848480"} borderRadius={"30px"} zIndex={200}>
                              <CardBody padding={"5px"}>
                                <HStack width={"full"} justifyContent={"center"} gap={"10px"}>
                                  {isExteriorCameraSelected &&
                                    !isMicrophoneSelected &&
                                    outCameras?.length > 1
                                    ? outCameras.map((camera) => (
                                      <PrimaryButton
                                        key={camera}
                                        backgroundColor={isCameraSelected(camera) ? "#5B5F6177" : "#F1F1F177"}
                                        borderWidth={"1px"}
                                        borderColor={isCameraSelected(camera) ? "#F1F1F177" : "transparent"}
                                        _hover={{
                                          backgroundColor: "#F1F1F1CC"
                                        }}
                                        borderRadius={"20px"}
                                        isSelected={isCameraSelected(camera)}
                                        onClick={() => onSelectCamera(camera)}
                                        size={"xs"}
                                        title={camera?.[0]?.toUpperCase() + camera?.slice(1)}
                                        zIndex={200}
                                      />
                                    ))
                                    : isInteriorCameraSelected &&
                                      !isMicrophoneSelected &&
                                      inCameras?.length > 1
                                      ? inCameras.map((camera) => (
                                        <PrimaryButton
                                          key={camera}
                                          borderRadius={"20px"}
                                          backgroundColor={isCameraSelected(camera) ? "#5B5F6177" : "#F1F1F177"}
                                          borderWidth={"1px"}
                                          borderColor={isCameraSelected(camera) ? "#F1F1F177" : "transparent"}
                                          _hover={{
                                            backgroundColor: "#F1F1F1CC"
                                          }}
                                          isSelected={isCameraSelected(camera)}
                                          onClick={() => onSelectCamera(camera)}
                                          size={"xs"}
                                          title={camera.toUpperCase()}
                                        />
                                      ))
                                      : isMicrophoneSelected && microphones?.length > 1
                                        ? microphones.map((microphone, index) => (
                                          <React.Fragment key={microphone.label}>
                                            <PrimaryButton
                                              borderRadius={"20px"}
                                              backgroundColor={!isMuted && microphoneSelected(microphone) ? "#5B5F6177" : "#F1F1F177"}
                                              borderWidth={"1px"}
                                              borderColor={!isMuted && microphoneSelected(microphone) ? "#F1F1F177" : "transparent"}
                                              _hover={{
                                                backgroundColor: "#F1F1F1CC"
                                              }}
                                              isSelected={
                                                !isMuted && microphoneSelected(microphone)
                                              }
                                              onClick={() => onSelectMicrophone(microphone)}
                                              size={"xs"}
                                              title={microphone.name.toUpperCase()}
                                            />
                                            {index === (microphones?.length - 1) / 2 && (
                                              <PrimaryButton
                                                borderRadius={"20px"}
                                                colorScheme={isMuted ? "cyan" : "gray"}
                                                isSelected={isMuted}
                                                onClick={() =>
                                                  isMuted
                                                    ? unMuteMicrophone()
                                                    : onMuteMicrophone()
                                                }
                                                size={"xs"}
                                                title={t(
                                                  "plugin.microphone.mute",
                                                ).toUpperCase()}
                                              />
                                            )}
                                          </React.Fragment>
                                        ))
                                        : null}
                                </HStack>
                              </CardBody>
                            </Card>
                          )}
                        <Card backgroundColor={"#84848480"} borderRadius={"30px"} zIndex={200}>
                          <CardBody padding={"5px"}>
                            <HStack justifyContent={"flex-start"} gap={"7px"}>
                              <PrimaryButton
                                borderRadius={"full"}
                                width={"40px"}
                                height={"40px"}
                                backgroundColor={isInteriorCameraSelected ? "#5B5F6177" : "#F1F1F177"}
                                borderWidth={"1px"}
                                borderColor={isInteriorCameraSelected ? "#F1F1F177" : "transparent"}
                                _hover={{
                                  backgroundColor: "#F1F1F1CC"
                                }}
                                icon={InteriorCameraIcon}
                                isSelected={isInteriorCameraSelected}
                                onClick={() => {
                                  if (inCameras?.length > 1) {
                                    onSelectInteriorCameras();
                                  } else {
                                    onSelectCamera(inCameras?.[0]);
                                  }
                                  setIsExteriorCameraSelected(false);
                                  setIsInteriorCameraSelected(
                                    inCameras?.length > 1 ? (previous) => !previous : true,
                                  );
                                  setIsMicrophoneSelected(false);
                                }}
                                size={"md"}
                              />
                              <PrimaryButton
                                borderRadius={"full"}
                                width={"40px"}
                                height={"40px"}
                                backgroundColor={"#F1F1F177"}
                                borderWidth={"1px"}
                                borderColor={"transparent"}
                                _hover={{
                                  backgroundColor: "#F1F1F1CC"
                                }}
                                icon={isMuted ? FaMicrophoneSlash : MicrophoneIcon}
                                isSelected={isMicrophoneSelected}
                                onClick={() => {
                                  if (microphones?.length <= 1) {
                                    if (isMuted) {
                                      unMuteMicrophone();
                                    } else {
                                      onMuteMicrophone();
                                    }
                                  } else {
                                    setIsExteriorCameraSelected(false);
                                    setIsInteriorCameraSelected(false);
                                  }
                                  setIsMicrophoneSelected(
                                    microphones?.length > 1 ? (previous) => !previous : false,
                                  );
                                }}
                                size={"md"}
                              />
                              <PrimaryButton
                                borderRadius={"full"}
                                width={"40px"}
                                height={"40px"}
                                backgroundColor={"#F1F1F177"}
                                borderWidth={"1px"}
                                borderColor={"transparent"}
                                _hover={{
                                  backgroundColor: "#F1F1F1CC"
                                }}
                                icon={PhoneIcon}
                                onClick={() => {
                                  onOpen();
                                }}
                                size={"md"} />
                              <PrimaryButton
                                borderRadius={"full"}
                                width={"40px"}
                                height={"40px"}
                                backgroundColor={isExteriorCameraSelected ? "#5B5F6177" : "#F1F1F177"}
                                borderWidth={"1px"}
                                borderColor={isExteriorCameraSelected ? "#F1F1F177" : "transparent"}
                                _hover={{
                                  backgroundColor: "#F1F1F1CC"
                                }}
                                icon={ExteriorCameraIcon}
                                isSelected={isExteriorCameraSelected}
                                onClick={() => {
                                  if (!isExteriorCameraSelected) {
                                    if (outCameras?.length > 1) {
                                      onSelectExteriorCameras();
                                    } else {
                                      onSelectCamera(outCameras?.[0]);
                                    }
                                  }
                                  setIsExteriorCameraSelected(
                                    outCameras?.length > 1 ? (previous) => !previous : true,
                                  );
                                  setIsInteriorCameraSelected(false);
                                  setIsMicrophoneSelected(false);
                                }}
                                size={"md"}
                              />
                            </HStack>
                          </CardBody>
                        </Card>
                      </VStack>
                    </Box>
                  </Box>
                </GridItem>
                {
                  externalTiles.length > 0 && (
                    <GridItem colSpan={1} borderRadius={"7px"}>
                      <Grid templateRows={"auto"} templateColumns={"auto"} width={"full"} height={"full"} borderRadius={"7px"} gap={"5px"}>
                        {externalTiles.map((tileId) => (
                          <GridItem key={tileId} colSpan={1} rowSpan={1}>
                            <Box as={RemoteVideo} tileId={tileId} borderRadius={"7px"} />
                          </GridItem>
                        ))}
                      </Grid>
                    </GridItem>
                  )
                }
              </Grid>
            )}
        </Box>
      ) : null
      }
      <ParticipantInput isOpen={isOpen} onClose={onClose} onSubmit={onAddParticipantConfirm} />
    </PrimaryCard >
  );
};

export default React.memo(CameraCard);
